import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  FileText, Search, Loader2, TrendingUp, AlertCircle, CheckCircle2,
  Clock, ChevronRight, BarChart3, Plus, FileCheck, MessageSquare, Phone, Download, Truck,
  Gavel,
} from "lucide-react";
import api from "@/lib/api";
import DevisTab from "@/pages/DevisTab";
import FacturationParcTab from "@/pages/FacturationParcTab";
import RelancesTab from "@/pages/RelancesTab";

const STATUT_LABELS = {
  payee: "Payée",
  en_suspens: "En suspens",
  emise: "En suspens",
  en_retard: "En retard",
  partiel: "Partiel",
  annule: "Annulée",
  brouillon: "Brouillon",
};
const STATUT_COLORS = {
  payee: "bg-emerald-50 text-emerald-700 border-emerald-200",
  en_suspens: "bg-amber-50 text-amber-700 border-amber-200",
  emise: "bg-amber-50 text-amber-700 border-amber-200",
  en_retard: "bg-red-50 text-red-700 border-red-200",
  partiel: "bg-blue-50 text-blue-700 border-blue-200",
  annule: "bg-gray-100 text-gray-500 border-gray-200",
  brouillon: "bg-gray-50 text-gray-500 border-gray-200",
};

function fmtChf(n) {
  if (n == null) return "—";
  return n.toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(s) {
  if (!s) return "—";
  const [y, m, d] = s.split("-");
  return `${d}.${m}.${y}`;
}

function StatsTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRetardModal, setShowRetardModal] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/factures/stats");
        setStats(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="text-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  if (!stats) return null;

  const payee = stats.by_statut?.payee || { n: 0, total: 0 };
  const en_susp = stats.by_statut?.en_suspens || { n: 0, total: 0 };
  const partiel = stats.by_statut?.partiel || { n: 0, total: 0 };
  const retard = stats.en_retard;

  const maxMois = Math.max(...stats.ca_par_mois.map((c) => c.total), 1);

  return (
    <div className="space-y-6" data-testid="factures-stats">
      {/* 4 KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-5 border-emerald-200 bg-gradient-to-br from-emerald-50 to-transparent">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Payées</div>
              <div className="text-3xl font-bold text-emerald-900 mt-1">{payee.n}</div>
              <div className="text-xs text-emerald-700 mt-1 font-mono">CHF {fmtChf(payee.total)}</div>
            </div>
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          </div>
        </Card>
        <Card className="p-5 border-amber-200 bg-gradient-to-br from-amber-50 to-transparent">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">En suspens</div>
              <div className="text-3xl font-bold text-amber-900 mt-1">{en_susp.n}</div>
              <div className="text-xs text-amber-700 mt-1 font-mono">CHF {fmtChf(en_susp.total)}</div>
              <div className="text-[9px] text-amber-600 mt-0.5">reste à encaisser</div>
            </div>
            <Clock className="w-8 h-8 text-amber-400" />
          </div>
        </Card>
        <Card className="p-5 border-blue-200 bg-gradient-to-br from-blue-50 to-transparent">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">Partiel</div>
              <div className="text-3xl font-bold text-blue-900 mt-1">{partiel.n}</div>
              <div className="text-xs text-blue-700 mt-1 font-mono">CHF {fmtChf(partiel.total)}</div>
              <div className="text-[9px] text-blue-600 mt-0.5">reste à encaisser</div>
            </div>
            <BarChart3 className="w-8 h-8 text-blue-400" />
          </div>
        </Card>
        <Card
          className="p-5 border-red-200 bg-gradient-to-br from-red-50 to-transparent cursor-pointer hover:border-red-400 hover:shadow-md transition-all"
          onClick={() => retard.n > 0 && setShowRetardModal(true)}
          title="Cliquer pour voir et relancer les factures en retard"
          data-testid="dashboard-en-retard-card"
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-red-700 font-semibold flex items-center gap-1">
                En retard
                {retard.n > 0 && <ChevronRight className="w-3 h-3" />}
              </div>
              <div className="text-3xl font-bold text-red-900 mt-1">{retard.n}</div>
              <div className="text-xs text-red-700 mt-1 font-mono">CHF {fmtChf(retard.total)}</div>
              <div className="text-[9px] text-red-600 mt-0.5">
                {retard.n > 0 ? "cliquer pour relancer" : "échéance dépassée"}
              </div>
            </div>
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
        </Card>
      </div>

      {/* CA mensuel + Top clients */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-4 sm:p-5 border-gray-200 lg:col-span-2 min-w-0">
          <h3 className="font-heading font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#52a675]" /> Chiffre d&apos;affaires sur 12 mois
          </h3>
          <div className="space-y-1.5">
            {stats.ca_par_mois.map((m) => (
              <div key={m.mois} className="flex items-center gap-2 sm:gap-3 text-xs min-w-0">
                <div className="w-14 sm:w-16 font-mono text-gray-600 flex-shrink-0">{m.mois}</div>
                <div className="flex-1 min-w-0 bg-gray-100 rounded-full h-5 relative overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-[#52a675] to-[#3d8a5c] h-full rounded-full transition-all"
                    style={{ width: `${(m.total / maxMois) * 100}%` }}
                  />
                </div>
                <div className="w-20 sm:w-24 text-right font-mono font-semibold text-gray-900 flex-shrink-0 whitespace-nowrap">
                  <span className="hidden sm:inline">CHF </span>{fmtChf(m.total)}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-4 sm:p-5 border-gray-200 min-w-0">
          <h3 className="font-heading font-semibold text-gray-900 mb-4">Top 5 clients</h3>
          <div className="space-y-3">
            {stats.top_clients.map((c, i) => (
              <div key={c.client_id || i} className="flex items-center gap-2 sm:gap-3 min-w-0">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                  i === 0 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"
                }`}>{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">{c.label}</div>
                  <div className="text-xs text-gray-500">{c.n_factures} facture(s)</div>
                </div>
                <div className="text-sm font-mono font-semibold text-gray-900 flex-shrink-0 whitespace-nowrap">{fmtChf(c.total)}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <FacturesEnRetardDialog open={showRetardModal} onClose={() => setShowRetardModal(false)} />
    </div>
  );
}

function FacturesEnRetardDialog({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [totalReste, setTotalReste] = useState(0);
  const [sendingIds, setSendingIds] = useState(new Set());

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/factures-en-retard");
      setItems(data.items || []);
      setTotalReste(data.total_reste || 0);
    } catch (e) {
      toast.error("Erreur chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) reload();
  }, [open, reload]);

  const sendOne = async (f) => {
    if (!f.telephone) {
      toast.error("Aucun téléphone disponible pour ce client");
      return;
    }
    const confirmMsg =
      `Envoyer un SMS de relance au ${f.telephone} pour ${f.contact_label} ?\n\n` +
      `Facture ${f.numero} · ${f.reste_a_payer.toFixed(2)} CHF · ${f.jours_retard} jour(s) de retard\n\n` +
      `⚠️ Le SMS sera réellement envoyé.`;
    if (!window.confirm(confirmMsg)) return;
    setSendingIds((prev) => new Set([...prev, f.id]));
    try {
      const { data } = await api.post(`/factures/${f.id}/relance-sms`, {});
      toast.success(`SMS envoyé au ${data.telephone}`);
      // Mise à jour locale
      setItems((prev) =>
        prev.map((x) =>
          x.id === f.id
            ? { ...x, nb_relances: (x.nb_relances || 0) + 1, derniere_relance: data.relance }
            : x
        )
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur envoi SMS");
    } finally {
      setSendingIds((prev) => {
        const next = new Set(prev);
        next.delete(f.id);
        return next;
      });
    }
  };

  const sendAll = async () => {
    const candidats = items.filter((f) => f.telephone);
    if (candidats.length === 0) {
      toast.error("Aucune facture avec téléphone disponible");
      return;
    }
    if (!window.confirm(
      `Envoyer un SMS de relance à ${candidats.length} client(s) ?\n\n` +
      `⚠️ Tous les SMS seront RÉELLEMENT envoyés. Cette action n'est pas réversible.`
    )) return;
    let ok = 0;
    let ko = 0;
    for (const f of candidats) {
      setSendingIds((prev) => new Set([...prev, f.id]));
      try {
        await api.post(`/factures/${f.id}/relance-sms`, {});
        ok++;
      } catch {
        ko++;
      } finally {
        setSendingIds((prev) => {
          const next = new Set(prev);
          next.delete(f.id);
          return next;
        });
      }
    }
    toast.success(`${ok} SMS envoyé(s)${ko > 0 ? ` · ${ko} échec(s)` : ""}`);
    await reload();
  };

  const fmtDateRel = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")} à ${String(d.getHours()).padStart(2, "0")}h${String(d.getMinutes()).padStart(2, "0")}`;
    } catch {
      return "—";
    }
  };

  const nbAvecTel = items.filter((f) => f.telephone).length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="factures-en-retard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            Factures en retard ({items.length})
          </DialogTitle>
          <DialogDescription>
            {totalReste > 0 && (
              <>Reste à encaisser : <strong className="text-red-700">CHF {fmtChf(totalReste)}</strong> · {nbAvecTel} client(s) avec téléphone joignable</>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-16 text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-gray-400"><CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-400" /><p>Aucune facture en retard 🎉</p></div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
              <span className="text-amber-900">
                💡 Le SMS contiendra automatiquement le n° facture, le montant et le nombre de jours de retard.
              </span>
              {nbAvecTel > 1 && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-400 text-red-700 hover:bg-red-50"
                  onClick={sendAll}
                  disabled={sendingIds.size > 0}
                  data-testid="relance-send-all-btn"
                >
                  <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Tout envoyer ({nbAvecTel})
                </Button>
              )}
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Facture</th>
                    <th className="px-3 py-2 text-left">Client</th>
                    <th className="px-3 py-2 text-right">Reste</th>
                    <th className="px-3 py-2 text-right">Retard</th>
                    <th className="px-3 py-2 text-left">Téléphone</th>
                    <th className="px-3 py-2 text-left">Dernière relance</th>
                    <th className="px-3 py-2 w-32"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((f) => {
                    const isSending = sendingIds.has(f.id);
                    return (
                      <tr key={f.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`retard-row-${f.id}`}>
                        <td className="px-3 py-2 font-mono font-semibold text-gray-900">{f.numero}</td>
                        <td className="px-3 py-2 text-gray-700">{f.contact_label}</td>
                        <td className="px-3 py-2 text-right font-mono font-semibold text-red-700">{fmtChf(f.reste_a_payer)}</td>
                        <td className="px-3 py-2 text-right">
                          <Badge className="bg-red-100 text-red-700 border-red-200">{f.jours_retard}j</Badge>
                        </td>
                        <td className="px-3 py-2 text-gray-700">
                          {f.telephone ? (
                            <span className="flex items-center gap-1 text-xs"><Phone className="w-3 h-3 text-emerald-600" />{f.telephone}</span>
                          ) : (
                            <span className="text-gray-300 italic text-xs">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-600 text-xs">
                          {f.nb_relances > 0 ? (
                            <span>{fmtDateRel(f.derniere_relance?.date)} <span className="text-gray-400">({f.nb_relances})</span></span>
                          ) : (
                            <span className="text-gray-300 italic">jamais</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => sendOne(f)}
                            disabled={!f.telephone || isSending}
                            className="text-xs h-7 border-red-300 text-red-700 hover:bg-red-50"
                            data-testid={`relance-send-btn-${f.id}`}
                          >
                            {isSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <><MessageSquare className="w-3 h-3 mr-1" /> Relancer</>}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ListeTab() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [statut, setStatut] = useState("all");
  const [typeClientTarif, setTypeClientTarif] = useState("all");  // iter173
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(new Set()); // iter77 — sélection multiple
  const [downloading, setDownloading] = useState(false);
  const limit = 50;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * limit, limit };
      if (q) params.q = q;
      if (statut && statut !== "all") params.statut = statut;
      if (typeClientTarif && typeClientTarif !== "all") params.type_client_tarif = typeClientTarif;
      const { data: resp } = await api.get("/factures", { params });
      setData(resp);
    } finally {
      setLoading(false);
    }
  }, [q, statut, typeClientTarif, page]);

  useEffect(() => {
    const t = setTimeout(fetchData, 200);
    return () => clearTimeout(t);
  }, [fetchData]);

  // iter77 — reset sélection quand le filtre change ou la page recharge
  useEffect(() => {
    setSelected(new Set());
  }, [q, statut, typeClientTarif, page]);

  const allSelectableIds = (data.items || [])
    .filter((f) => f.statut !== "annule" && f.statut !== "brouillon")
    .map((f) => f.id);
  const allSelected = allSelectableIds.length > 0 && allSelectableIds.every((id) => selected.has(id));

  const toggleOne = (id, e) => {
    e?.stopPropagation();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => {
      if (prev.size === allSelectableIds.length) return new Set();
      return new Set(allSelectableIds);
    });
  };

  // iter332 — Compteur des montants sélectionnés (dès 2 factures cochées)
  const selectionTotals = (data.items || []).reduce(
    (acc, f) => {
      if (!selected.has(f.id)) return acc;
      acc.n += 1;
      acc.brut += f.montant_brut || 0;
      acc.reste += f.statut === "annule" ? 0 : Math.max(0, f.reste_a_payer || 0);
      return acc;
    },
    { n: 0, brut: 0, reste: 0 },
  );

  const downloadBatch = async () => {
    if (selected.size === 0) return;
    setDownloading(true);
    try {
      const ids = Array.from(selected);
      const { data: blob } = await api.post(
        "/factures/download-batch",
        { facture_ids: ids },
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `factures-lot-${today}-${ids.length}f.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`PDF de ${ids.length} facture(s) téléchargé`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Erreur";
      toast.error(typeof msg === "string" ? msg : "Erreur téléchargement", { duration: 6000 });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid="factures-list">
      {/* iter256 — mobile : recherche pleine largeur, les 2 menus côte à côte, bouton sur la longueur */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative w-full sm:flex-1 sm:min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder="N°, client, plaque, véhicule, n° OR, châssis, référence…" className="pl-10" data-testid="facture-search" />
        </div>
        <Select value={statut} onValueChange={(v) => { setStatut(v); setPage(0); }}>
          <SelectTrigger className="flex-1 min-w-0 sm:flex-none sm:w-44" data-testid="facture-filter-statut">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="payee">Payées</SelectItem>
            <SelectItem value="en_suspens">En suspens</SelectItem>
            <SelectItem value="partiel">Partiel</SelectItem>
          </SelectContent>
        </Select>
        {/* iter173 — Filtre Type OR (particulier / assurance / flotte / interne) */}
        <Select value={typeClientTarif} onValueChange={(v) => { setTypeClientTarif(v); setPage(0); }}>
          <SelectTrigger className="flex-1 min-w-0 sm:flex-none sm:w-44" data-testid="facture-filter-type">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous types</SelectItem>
            <SelectItem value="assurance">Assurance</SelectItem>
            <SelectItem value="flotte">Flotte</SelectItem>
            <SelectItem value="particulier">Particulier</SelectItem>
            <SelectItem value="interne">Interne</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => navigate("/facturation/nouveau")} className="bg-[#52a675] hover:bg-[#3d8a5c] w-full sm:w-auto" data-testid="facture-new-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle facture
        </Button>
        {selected.size > 0 && (
          <Button
            onClick={downloadBatch}
            disabled={downloading}
            variant="outline"
            className="border-[#52a675]/30 text-[#3d8a5c] hover:bg-[#f0f9f4] w-full sm:w-auto"
            data-testid="facture-batch-download-btn"
          >
            {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Télécharger PDF ({selected.size})
          </Button>
        )}
      </div>

      {selectionTotals.n >= 2 && (
        <div
          className="mb-3 flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg border border-[#52a675]/30 bg-[#f0f9f4] px-4 py-2.5"
          data-testid="facture-selection-total"
        >
          <span className="text-sm font-semibold text-[#2f6b47]" data-testid="facture-selection-count">
            {selectionTotals.n} factures sélectionnées
          </span>
          <span className="text-sm text-gray-700">
            Total&nbsp;
            <span className="font-mono font-bold text-gray-900" data-testid="facture-selection-brut">
              CHF {fmtChf(selectionTotals.brut)}
            </span>
          </span>
          {selectionTotals.reste > 0.01 && (
            <span className="text-sm text-gray-700">
              Reste à encaisser&nbsp;
              <span className="font-mono font-bold text-amber-700" data-testid="facture-selection-reste">
                CHF {fmtChf(selectionTotals.reste)}
              </span>
            </span>
          )}
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="ml-auto text-xs text-gray-500 underline hover:text-gray-800"
            data-testid="facture-selection-clear"
          >
            Tout désélectionner
          </button>
        </div>
      )}

      <Card className="border-gray-200 overflow-hidden">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-3 py-2 w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    className="rounded border-gray-300 text-[#52a675] focus:ring-[#52a675]"
                    data-testid="facture-select-all"
                    title="Tout sélectionner / désélectionner"
                  />
                </th>
                <th className="px-3 py-2 font-semibold text-xs uppercase tracking-wider text-gray-500">N° facture</th>
                <th className="px-3 py-2 font-semibold text-xs uppercase tracking-wider text-gray-500">Date</th>
                <th className="px-3 py-2 font-semibold text-xs uppercase tracking-wider text-gray-500">Client</th>
                <th className="px-3 py-2 font-semibold text-xs uppercase tracking-wider text-gray-500">Véhicule / OR</th>
                <th className="px-3 py-2 font-semibold text-xs uppercase tracking-wider text-gray-500">Statut</th>
                <th className="px-3 py-2 font-semibold text-xs uppercase tracking-wider text-gray-500 text-right">Montant brut</th>
                <th className="px-3 py-2 font-semibold text-xs uppercase tracking-wider text-gray-500 text-right">Reste</th>
                <th className="px-3 py-2 font-semibold text-xs uppercase tracking-wider text-gray-500">Échéance</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} className="text-center py-8 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></td></tr>
              )}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={10} className="text-center py-12 text-gray-400">Aucune facture trouvée</td></tr>
              )}
              {!loading && data.items.map((f) => (
                <tr key={f.id} onClick={() => navigate(`/facturation/${f.id}`)} className="border-t border-gray-100 hover:bg-[#f0f9f4]/50 cursor-pointer" data-testid={`facture-row-${f.numero}`}>
                  <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(f.id)}
                      onChange={(e) => toggleOne(f.id, e)}
                      disabled={f.statut === "annule" || f.statut === "brouillon"}
                      className="rounded border-gray-300 text-[#52a675] focus:ring-[#52a675] disabled:opacity-30"
                      data-testid={`facture-select-${f.numero}`}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td className="px-3 py-2.5">
                    <code className="text-xs font-mono font-semibold text-gray-900">{f.numero}</code>
                    {f.origine === "natif_or" && f.or_id ? (
                      <Badge variant="outline" className="ml-2 bg-blue-50 text-blue-700 border-blue-200 text-[9px] py-0 px-1" title="Facture émise depuis un OR atelier">
                        Atelier
                      </Badge>
                    ) : f.origine === "natif" ? (
                      <Badge variant="outline" className="ml-2 bg-gray-50 text-gray-700 border-gray-200 text-[9px] py-0 px-1" title="Facture créée directement depuis Facturation">
                        Direct
                      </Badge>
                    ) : f.origine === "bexio" ? (
                      <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-700 border-amber-200 text-[9px] py-0 px-1" title="Importée depuis Bexio">
                        Bexio
                      </Badge>
                    ) : null}
                    {f.type_client_tarif === "assurance" && (
                      <Badge variant="outline" className="ml-1 bg-amber-100 text-amber-800 border-amber-300 text-[9px] py-0 px-1 font-semibold" title="Facture destinée à une assurance" data-testid={`facture-assurance-badge-${f.numero}`}>
                        Assurance
                      </Badge>
                    )}
                    {f.type_client_tarif === "flotte" && (
                      <Badge variant="outline" className="ml-1 bg-purple-50 text-purple-700 border-purple-200 text-[9px] py-0 px-1" title="Facture flotte">
                        Flotte
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-gray-600">{fmtDate(f.date_emission)}</td>
                  <td className="px-3 py-2.5 text-gray-800">{f.contact_label}</td>
                  {/* iter229 — Véhicule / OR lié : permet de retrouver et reconnaître une facture par plaque */}
                  <td className="px-3 py-2.5 text-xs" data-testid={`facture-vehicule-${f.numero}`}>
                    {f.plaque ? (
                      <code className="font-mono font-semibold text-gray-800">{f.plaque}</code>
                    ) : f.vehicule_label ? (
                      <span className="text-gray-700">{f.vehicule_label}</span>
                    ) : <span className="text-gray-300">—</span>}
                    {f.or_numero && (
                      <div className="text-[10px] text-gray-400">OR {f.or_numero}</div>
                    )}
                    {!f.plaque && f.reference_libre && (
                      <div className="text-[10px] text-gray-400 truncate max-w-[160px]" title={f.reference_libre}>
                        Réf. {f.reference_libre}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5"><Badge variant="outline" className={STATUT_COLORS[f.statut]}>{STATUT_LABELS[f.statut]}</Badge></td>
                  <td className="px-3 py-2.5 text-right font-mono">{fmtChf(f.montant_brut)}</td>
                  <td className="px-3 py-2.5 text-right font-mono">
                    {f.statut !== "annule" && f.reste_a_payer > 0.01 ? <span className="text-amber-700 font-semibold">{fmtChf(f.reste_a_payer)}</span> : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-gray-600">{fmtDate(f.date_echeance)}</td>
                  <td className="px-3 py-2.5 text-right"><ChevronRight className="w-4 h-4 text-gray-300" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* iter256 — mobile : pas d'en-tête de table, 2 lignes par facture
            (n°, date, client, véhicule, statut, montant brut) */}
        <div className="md:hidden divide-y divide-gray-100" data-testid="factures-list-mobile">
          {loading && (
            <div className="text-center py-8 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          )}
          {!loading && data.items.length === 0 && (
            <div className="text-center py-12 text-gray-400 text-sm">Aucune facture trouvée</div>
          )}
          {!loading && data.items.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => navigate(`/facturation/${f.id}`)}
              className="w-full text-left px-3 py-2.5 hover:bg-[#f0f9f4]/50 active:bg-[#f0f9f4]"
              data-testid={`facture-card-${f.numero}`}
            >
              <div className="flex items-center justify-between gap-2 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <code className="text-xs font-mono font-semibold text-gray-900">{f.numero}</code>
                  <span className="text-[11px] text-gray-500 whitespace-nowrap">{fmtDate(f.date_emission)}</span>
                </div>
                <Badge variant="outline" className={`${STATUT_COLORS[f.statut]} text-[10px] flex-shrink-0`}>
                  {STATUT_LABELS[f.statut]}
                </Badge>
              </div>
              <div className="flex items-end justify-between gap-2 min-w-0 mt-0.5">
                <div className="min-w-0">
                  <div className="text-sm text-gray-800 truncate">{f.contact_label}</div>
                  <div className="text-[11px] text-gray-500 truncate">
                    {f.plaque || f.vehicule_label || "—"}
                    {f.or_numero ? ` · OR ${f.or_numero}` : ""}
                  </div>
                </div>
                <div className="font-mono text-sm font-semibold text-gray-900 flex-shrink-0 whitespace-nowrap">
                  {fmtChf(f.montant_brut)}
                </div>
              </div>
            </button>
          ))}
        </div>
        {data.total > limit && (
          <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100 text-xs text-gray-600">
            <div>{data.skip + 1} – {Math.min(data.skip + limit, data.total)} / {data.total}</div>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Précédent</Button>
              <Button size="sm" variant="outline" disabled={(page + 1) * limit >= data.total} onClick={() => setPage((p) => p + 1)}>Suivant</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

function AEmettreTab() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/or", { params: { statut: "pret", limit: 200 } });
      // iter92 : seuls les OR `pret` SANS facture_id sont "à émettre".
      // iter127 : exclure les OR Parc (facturés en groupe via onglet "Parc").
      const aEmettre = (data.items || []).filter((or) => !or.facture_id && !or.is_parc);
      setItems(aEmettre);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="text-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  return (
    <div data-testid="factures-aemettre-tab">
      {items.length === 0 ? (
        <Card className="p-8 border-gray-200 text-center text-sm text-gray-500">
          <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
          Aucune facture à émettre — tous les dossiers terminés sont facturés.
        </Card>
      ) : (
        <>
          <div className="p-3 mb-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
            <strong>{items.length}</strong> facture(s) à émettre. Ouvrez le dossier pour éditer
            la facture (lignes, prix, en-tête, infos client / véhicule) puis choisissez le canal :
            Caisse · Mail · Lixt · Courrier · Hybride.
          </div>

          {/* Mobile — cartes */}
          <div className="sm:hidden space-y-2">
            {items.map((or) => (
              <Card
                key={or.id}
                className="p-3.5 border-amber-200/70 active:bg-amber-50 cursor-pointer"
                onClick={() => navigate(`/facturation/a-emettre/${or.id}`)}
                data-testid={`aemettre-card-${or.numero}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <code className="text-xs font-mono font-semibold text-gray-900">{or.numero}</code>
                  <span className="font-mono font-bold text-gray-900">{or.totaux?.total_ttc?.toFixed(2)} CHF</span>
                </div>
                <div className="text-sm text-gray-800 mt-1.5 font-medium">{or.client_nom}</div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-gray-500">{or.vehicule_label}</span>
                  <span className="text-xs text-amber-700 font-semibold inline-flex items-center">
                    Éditer & émettre <ChevronRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </Card>
            ))}
          </div>

          {/* Desktop — table */}
          <Card className="border-gray-200 overflow-hidden hidden sm:block">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">OR</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Client</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Véhicule</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Montant TTC</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((or) => (
                  <tr
                    key={or.id}
                    className="border-t border-gray-100 hover:bg-amber-50/40 cursor-pointer"
                    onClick={() => navigate(`/facturation/a-emettre/${or.id}`)}
                    data-testid={`aemettre-${or.numero}`}
                  >
                    <td className="px-3 py-2.5 font-mono font-semibold text-xs">{or.numero}</td>
                    <td className="px-3 py-2.5 text-gray-800">{or.client_nom}</td>
                    <td className="px-3 py-2.5 text-xs text-gray-600">{or.vehicule_label}</td>
                    <td className="px-3 py-2.5 text-right font-mono font-semibold">{or.totaux?.total_ttc?.toFixed(2)}</td>
                    <td className="px-3 py-2.5 text-right">
                      <Button
                        size="sm"
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        onClick={(e) => { e.stopPropagation(); navigate(`/facturation/a-emettre/${or.id}`); }}
                        data-testid={`aemettre-edit-${or.numero}`}
                      >
                        Éditer & émettre <ChevronRight className="w-3 h-3 ml-1" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

export default function Facturation() {
  // iter127 — Permet la nav depuis OR Parc terminé : /facturation { state: { tab: "parc" } }
  const location = useLocation();
  // iter234 — Deep-link depuis l'historique client : /facturation?devis=<id>
  const devisParam = new URLSearchParams(location.search).get("devis");
  const initialTab = location.state?.tab || (devisParam ? "devis" : "liste");
  // iter254 — sur mobile la bande d'onglets devient un menu déroulant
  const [tab, setTab] = useState(initialTab);
  const TABS = [
    { value: "liste", label: "Factures", icon: FileText },
    { value: "relances", label: "Relances", icon: Gavel },
    { value: "devis", label: "Devis", icon: FileCheck },
    { value: "a_emettre", label: "À émettre", icon: AlertCircle },
    { value: "parc", label: "Parc", icon: Truck },
    { value: "stats", label: "Tableau de bord", icon: TrendingUp },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="facturation-page">
      <h1 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 flex items-center gap-2 mb-1">
        <FileText className="w-7 h-7 text-[#52a675]" /> Facturation
      </h1>
      <p className="text-sm text-gray-500 mb-6">
        Suivi des factures clients · encaissements · QR-bill
      </p>

      <Tabs value={tab} onValueChange={setTab}>
        {/* Mobile : menu déroulant */}
        <div className="sm:hidden mb-3">
          <Select value={tab} onValueChange={setTab}>
            <SelectTrigger className="w-full" data-testid="facturation-tab-menu">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {/* iter256 — le menu « Relances » n'est pas proposé sur mobile */}
              {TABS.filter((t) => t.value !== "relances").map((t) => (
                <SelectItem key={t.value} value={t.value} data-testid={`facturation-tab-menu-${t.value}`}>
                  <span className="flex items-center gap-2"><t.icon className="w-4 h-4" /> {t.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <TabsList className="hidden sm:flex w-full overflow-x-auto justify-start no-scrollbar flex-nowrap">
          <TabsTrigger value="liste" data-testid="tab-factures-liste"><FileText className="w-4 h-4 mr-2" /> Factures</TabsTrigger>
          <TabsTrigger value="relances" data-testid="tab-factures-relances"><Gavel className="w-4 h-4 mr-2" /> Relances</TabsTrigger>
          <TabsTrigger value="devis" data-testid="tab-factures-devis"><FileCheck className="w-4 h-4 mr-2" /> Devis</TabsTrigger>
          <TabsTrigger value="a_emettre" data-testid="tab-factures-aemettre"><AlertCircle className="w-4 h-4 mr-2" /> À émettre</TabsTrigger>
          <TabsTrigger value="parc" data-testid="tab-factures-parc"><Truck className="w-4 h-4 mr-2" /> Parc</TabsTrigger>
          <TabsTrigger value="stats" data-testid="tab-factures-stats"><TrendingUp className="w-4 h-4 mr-2" /> Tableau de bord</TabsTrigger>
        </TabsList>
        <TabsContent value="liste" className="mt-4"><ListeTab /></TabsContent>
        <TabsContent value="relances" className="mt-4"><RelancesTab /></TabsContent>
        <TabsContent value="devis" className="mt-4"><DevisTab initialDevisId={devisParam} /></TabsContent>
        <TabsContent value="a_emettre" className="mt-4"><AEmettreTab /></TabsContent>
        <TabsContent value="parc" className="mt-4"><FacturationParcTab /></TabsContent>
        <TabsContent value="stats" className="mt-4"><StatsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
